import { memo } from 'react'
import dynamic from 'next/dynamic'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import type { tProduct } from 'src/shared/frontendTypes/product'
import type { combination } from './functions/getCombination'
import type { selectedAttributes } from './Components/AddToCart/Components/AttributesSelector'
import { useProductRecommendations } from 'src/hooks/useProductRecommendations'
import { useRecommendationReveal } from './useRecommendationReveal'
import { pageBuilderOutlinedButtonSx } from 'src/theme/pageBuilderButtons'
import { PurchasedAccordion } from './Components/DescriptionAccordion/DescriptionAccordion'

const CrossSellSections = dynamic(() => import('./Components/CrossSellSections/CrossSellSections'))

// Recommendation responses and the reveal state only update this region. The
// main product, its schema and purchase controls do not depend on that request.
const ProductRecommendations: React.FC<{
  product: tProduct
  deferred: boolean
  combination: combination | null
  selectedAttributes: selectedAttributes
}> = ({ product, deferred, combination, selectedAttributes }) => {
  const options = combination?.options?.length
    ? combination.options
    : Object.values(selectedAttributes).filter((value): value is number => typeof value === 'number')
  const recommendations = useProductRecommendations(product.id, options, deferred)
  const layout = useRecommendationReveal(deferred, Boolean(recommendations.data))
  const hasRecommendations = Boolean(recommendations.data?.alsoPurchased.length
    || recommendations.data?.crossSellSections.some(section => section.products.length)
    || recommendations.data?.dynamicSerie?.section?.products.length)
  const crossSellSections = deferred ? recommendations.data?.crossSellSections || [] : product.crossSellSections
  const alsoPurchased = deferred ? recommendations.data?.alsoPurchased || [] : product.alsoPurchased

  return <div ref={layout.anchorRef} style={{ display: 'flow-root' }}>
    <Box aria-label='Produktempfehlungen' aria-busy={recommendations.loading}>
      {!layout.showRecommendations && <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 64, boxSizing: 'border-box', py: 1 }}
      >
        {recommendations.loading && <Box role='status' sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={20} aria-hidden='true' />
          <span>Empfehlungen werden geladen …</span>
        </Box>}
        {recommendations.error && <Button variant='outlined' sx={pageBuilderOutlinedButtonSx} onClick={recommendations.retry}>
          Empfehlungen erneut laden
        </Button>}
        {recommendations.data && (hasRecommendations
          ? <Button variant='outlined' sx={pageBuilderOutlinedButtonSx} onClick={layout.reveal}>
              Empfehlungen anzeigen
            </Button>
          : <Typography role='status'>Keine weiteren Empfehlungen verfügbar.</Typography>)}
      </Box>}
      {layout.showRecommendations && <>
        {deferred && <PurchasedAccordion product={product} alsoPurchased={alsoPurchased} />}
        <CrossSellSections
          key={product.id}
          product={product}
          crossSellSections={crossSellSections}
          initialDynamicSerie={recommendations.data?.dynamicSerie}
          recommendationsReady={!deferred || Boolean(recommendations.data)}
          selectedAttributes={selectedAttributes}
          combination={combination}
        />
      </>}
    </Box>
  </div>
}

export default memo(ProductRecommendations)
